import {
  BrandkitEntity,
  DivisionEntity,
  CompanyEntity,
  UserEntity,
  DivisionTranslationEntity,
  DivisionTypeEnum,
  FontDefinitionDto,
  PagesEntryBehaviorEnum,
  PreferredColorThemeEnum,
  DivisionBehaviour,
  LanguageEntity,
  DocumentDefinitionDto,
  MarkdownTextDto,
  WhitelabelEntity,
} from "@citychallenge/api-public";
import { observable, action, computed } from "mobx";
import { GameStore } from "./game-store";
import { ArticleStore } from "./article-store";
import { EventStore } from "./event.store";
import { OnDemandStore } from "./on-demand-store";
import { Loadable } from "../loadable";
import { Environment } from "../environment";
import { Types } from "@citychallenge/common";

/**
 * `contentTypeNames` carries the brand's and the organization's own wording for
 * content types, which the player labels tabs and cards with. The
 * organization's wins over the brand's — merged in `useContentNames`.
 */
type PublicCompany = Pick<CompanyEntity, "name" | "contentTypeNames"> & {
  whitelabel: Pick<WhitelabelEntity, "name" | "bundleId" | "contentTypeNames">;
};

interface PublicDivisionEntity extends Omit<DivisionEntity, "company"> {
  company?: PublicCompany;
}

export interface DivisionData extends PublicDivisionEntity {
  authError?: boolean;
}

export class DivisionModel implements Omit<PublicDivisionEntity, "company"> {
  public id!: string;

  public type = DivisionTypeEnum.Division;

  @observable
  public name!: string;

  @observable
  public company?: PublicCompany;

  @observable
  public code!: string;

  @observable
  public color?: string;

  @observable
  public colorBackground?: string;

  @observable
  public colorText?: string;

  @observable
  public fontPrimary?: FontDefinitionDto | undefined;

  @observable
  public fontSecondary?: FontDefinitionDto | undefined;

  @observable
  public icon?: string;

  @observable
  public description?: Types["MarkdownText"];

  @observable
  public contact?: UserEntity;

  @observable
  public contactButtonTitle?: string;

  @observable
  public contactPageText?: MarkdownTextDto;

  @observable
  public contactPageImage?: string;

  public baseLanguage?: LanguageEntity;

  @observable
  public translations?: Array<DivisionTranslationEntity>;

  @observable
  public brandkits?: Array<BrandkitEntity>;

  public defaultBrandkit?: BrandkitEntity;

  public preferredColorTheme?: PreferredColorThemeEnum;

  public hasContact = false;

  @computed
  public get hasContactPage() {
    if (this.contactPageText?.text) return true;

    if (this.hasContact) return true;

    return false;
  }

  @computed
  public get supportedLanguages() {
    const languages: string[] = [];

    if (this.baseLanguage) languages.push(this.baseLanguage.id);

    if (this.translations)
      for (const { language } of this.translations) {
        if (language) languages.push(language.id);
      }

    return languages;
  }

  public forceAuthentication?: boolean;

  public enableMfa?: boolean;

  @observable
  public experiencesTabTitle?: string;

  @observable
  public experiencesWelcomeText?: string;

  @observable
  public articlesEnabled?: boolean;

  @observable
  public articlesTabTitle?: string;

  /**
   * Workspace projects swap the classic dashboard for the pages-only
   * runtime (lazy-loaded in web), serving distributed pages.
   */
  @observable
  public workspaceEnabled?: boolean;

  /** The project builds its own pages, which swap the classic dashboard too. */
  @observable
  public pagesEnabled?: boolean;

  @observable
  public pagesEntryBehavior?: PagesEntryBehaviorEnum;

  @observable
  public pagesAccent?: string;

  @observable
  public onDemandEnabled?: boolean;

  @observable
  public onDemandTabTitle?: string;

  @observable
  public eventsEnabled?: boolean;

  @observable
  public eventsTabTitle?: string;

  @observable
  public disableEventCreation?: boolean;

  @observable
  public webinarsEnabled?: boolean;

  @observable
  public mapBased?: boolean;

  @observable
  public mapBasedDisableGps?: boolean;

  @observable
  public mapBasedTitle?: string;

  @observable
  public mapBasedShowLegend?: boolean;

  @observable
  public alwaysHideMap?: boolean;

  @observable
  public disableStoryOverview?: boolean;

  @observable
  public showLatestStoryVersion?: boolean;

  @observable
  public shareButtonEnabled?: boolean;

  @observable
  public selectableLanguages?: string[];

  @observable
  public customMapUrl?: string;

  @observable
  public behaviour?: DivisionBehaviour;

  @observable
  public privacyDocument?: DocumentDefinitionDto;

  @observable
  public tosDocument?: DocumentDefinitionDto;

  @observable
  public cookiePolicyDocument?: DocumentDefinitionDto;

  @observable
  public customPolicyDocument?: DocumentDefinitionDto;

  /**
   * Because the division mode is mutual exclusive between map, learning and
   * default (story overview). We have to determine the mode based on properties.
   */
  @computed
  public get dashboardMode() {
    if (this.mapBased) {
      return "map";
    }

    if (this.isLearning) {
      return "learning";
    }
    return "default";
  }
  @computed
  public get isLearning() {
    return (
      Environment.type === "learning" ||
      this.articlesEnabled ||
      this.eventsEnabled ||
      this.webinarsEnabled ||
      this.onDemandEnabled
    );
  }

  @computed
  public get environment() {
    return Environment.type;
  }

  @computed
  public get showArticlesTab() {
    return this.articlesEnabled;
  }

  @computed
  public get showOnDemandTab() {
    return this.onDemandEnabled;
  }

  @computed
  public get showEventsTab() {
    return this.eventsEnabled;
  }

  @computed
  public get showWebinarsTab() {
    return this.webinarsEnabled;
  }

  @computed
  public get showInlineMap() {
    return !this.alwaysHideMap;
  }

  constructor(data: PublicDivisionEntity) {
    this.assignData(data);
  }

  @action
  public update(data: PublicDivisionEntity) {
    this.assignData(data);
  }

  private assignData = (data: DivisionData) => {
    // FIX: Apparently these stores are exported to JSON definition, which should
    // not really happen because it overrides the way the stores are supposed
    // to work (with mobx handling the state changes)
    delete (data as any).gameStore;
    delete (data as any).articleStore;
    delete (data as any).eventStore;
    delete (data as any).workspaceEventStore;
    delete (data as any).onDemandStore;

    Object.assign(this, data);
  };

  @observable
  public authError = false;

  public loadableGameStore?: Loadable<GameStore>;
  public readonly gameStore = new GameStore([]);

  public loadableArticleStore?: Loadable<ArticleStore>;
  public readonly articleStore = new ArticleStore([]);

  public loadableEventStore?: Loadable<EventStore>;
  public readonly eventStore = new EventStore([]);

  /**
   * Events surfaced through the division's pages runtime (members of its
   * embedded collections, usually org-owned) — separate from the division's
   * own eventStore.
   */
  public loadableWorkspaceEventStore?: Loadable<EventStore>;
  public readonly workspaceEventStore = new EventStore([]);

  public loadableOnDemandStore?: Loadable<OnDemandStore>;
  public readonly onDemandStore = new OnDemandStore([]);
}
